import React, { useEffect, useState } from "react";
import {
  ExclamationTriangleIcon,
  XCircleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
} from "@heroicons/react/20/solid";
import CloseButton from "../closeButton/CloseButton";

interface SnackBarProps {
  title?: string;
  body?: React.ReactNode;
  /** Optional control (e.g. an "Undo" button) rendered to the right of the text, before the close button. */
  action?: React.ReactNode;
  variant?: "success" | "error" | "info" | "warning";
  icon?: boolean;
  isVisible?: boolean;
  onClose?: () => void;
  fullWidth?: boolean;
  persist?: boolean;
  style?: React.CSSProperties;
  index?: number;
  hook?: boolean;
}

const VARIANT_CLASSES = {
  warning: {
    bg: "bg-amber-100",
    border: "border-amber-700",
    text: "text-amber-800",
    icon: <ExclamationTriangleIcon className="h-5 w-5 text-amber-700" />,
  },
  error: {
    bg: "bg-red-100",
    border: "border-red-600",
    text: "text-red-800",
    icon: <XCircleIcon className="h-5 w-5 text-red-600" />,
  },
  info: {
    bg: "bg-blue-100",
    border: "border-blue-600",
    text: "text-blue-800",
    icon: <InformationCircleIcon className="h-5 w-5 text-blue-600" />,
  },
  success: {
    bg: "bg-emerald-100",
    border: "border-emerald-600",
    text: "text-emerald-800",
    icon: <CheckCircleIcon className="h-5 w-5 text-emerald-600" />,
  },
};

const SnackBar = ({
  title,
  body,
  action,
  variant = "success",
  icon = true,
  isVisible = true,
  onClose,
  fullWidth = true,
  persist = false,
  hook = false,
}: SnackBarProps) => {
  const [isFadingOut, setIsFadingOut] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setIsFadingOut(false);
    }
  }, [isVisible]);

  useEffect(() => {
    if (isVisible && !persist) {
      const timer = setTimeout(() => {
        handleClose();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, persist]);

  const handleClose = () => {
    if (isFadingOut) return;
    setIsFadingOut(true);
    setTimeout(() => {
      onClose?.();
    }, 500);
  };

  if (!isVisible) return null;

  const classes = VARIANT_CLASSES[variant] || VARIANT_CLASSES.success;
  const animationClass = isFadingOut ? "animate-fade-out" : "animate-slide-down";
  const hookClass = hook ? "" : "fixed top-5 left-0 right-0 z-50";
  const hasBody = Boolean(body);

  return (
    <div className={hookClass}>
      <div className="max-w-screen-xl mx-auto px-4">
        <div
          className={`${fullWidth ? "w-full" : "w-fit mx-auto"} border-l-4 ${classes.border} ${
            classes.bg
          } p-4 rounded-md shadow-md ${animationClass} font-sans`}
        >
          <div className={`flex gap-3 ${hasBody ? "items-start" : "items-center"}`}>
            {icon && <div className="shrink-0">{classes.icon}</div>}
            <div className="flex-1 min-w-0">
              {title && <p className={`m-0 font-medium leading-5 ${classes.text}`}>{title}</p>}
              {body && <div className={`mt-1 text-sm leading-5 ${classes.text}`}>{body}</div>}
            </div>
            {action && <div className={`shrink-0 ${classes.text}`}>{action}</div>}
            <div className="shrink-0">
              <CloseButton onClick={handleClose} colorClass={classes.text} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SnackBar;
